import {
  IBoundingBoxResult,
  computeBoundingBoxHelper,
} from "../computeBoundingBoxHelper";
import { Mesh, Object3D, Vector3 } from "three";

import { Holder3D } from "../../../types/holder3d";
import { IPartsChildren } from "../../../types/IPartsConfigSchema";

export function addParts({
  conf,
  holder3D,
  cells,
  partsLibrary,
  MAX_WIDTH,
  MIN_WIDTH,
  overridePos,
  overrideRot,
}: {
  holder3D: Holder3D;
  readonly conf: IPartsChildren;
  readonly cells: number;
  readonly partsLibrary: Record<string, Mesh | Object3D>;
  readonly MAX_WIDTH?: number;
  readonly MIN_WIDTH?: number;
  readonly overridePos?: Vector3;
  readonly overrideRot?: Vector3;
}): {
  measures: Record<string, IBoundingBoxResult>;
  partsGroups: Record<string, string[]>;
} {
  const measures: Record<string, IBoundingBoxResult> = {};
  const partsGroups: Record<string, string[]> = {};

  conf.parts.forEach((part) => {
    if (typeof part === "string") {
      const partName = part;
      const partObject3D =
        partsLibrary[partName] && partsLibrary[partName].clone();

      if (!partObject3D) {
        console.error(`Part "${partName}" doesn't exist in Model`);
        throw new Error(`Part "${partName}" doesn't exist in Model`);
      }

      if (
        partObject3D &&
        (partObject3D as Mesh).morphTargetInfluences &&
        MAX_WIDTH !== undefined &&
        MIN_WIDTH !== undefined
      ) {
        (partObject3D as Mesh).updateMorphTargets();
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        (partObject3D as Mesh).morphTargetInfluences![0] =
          (cells - MIN_WIDTH) / (MAX_WIDTH - MIN_WIDTH);
      }

      if (overridePos) {
        partObject3D.position.copy(overridePos);
      }

      if (overrideRot) {
        partObject3D.rotation.x = overrideRot.x;
        partObject3D.rotation.y = overrideRot.y;
        partObject3D.rotation.z = overrideRot.z;
      }

      holder3D.add3D(partName, partObject3D);
      const objMeasures = computeBoundingBoxHelper(partObject3D);
      if (objMeasures) measures[partObject3D.uuid] = objMeasures;
    } else {
      if (part.isGroup) {
        const partName = part.name;
        const childHolder = new Holder3D(partName);
        holder3D.add3D(partName, childHolder);

        const { measures: measuresRec, partsGroups: partGroupsRec } = addParts({
          conf: part,
          holder3D: childHolder,
          cells,
          partsLibrary,
        });

        Object.assign(measures, measuresRec);
        Object.assign(partsGroups, partGroupsRec);

        if (part.globalGroup) {
          addToGroup(partName, part.globalGroup);
        }

        if (part.resetPos) {
          if (typeof part.resetPos === "boolean") {
            childHolder.position = new Vector3();
          } else {
            childHolder.position = part.resetPos;
          }
        }
      } else if (
        part.isOption &&
        cells >= part.min &&
        cells <= part.max &&
        part.part
      ) {
        const partName = part.part;

        const partObject3D =
          partsLibrary[partName] && partsLibrary[partName].clone();

        if (!partObject3D) {
          console.error(`Part "${partName}" doesn't exist in Model`);
          throw new Error(`Part "${partName}" doesn't exist in Model`);
        }

        partObject3D.name = part.partName || partName;

        if ((partObject3D as Mesh).morphTargetInfluences) {
          (partObject3D as Mesh).updateMorphTargets();

          const min = part.minCalc !== undefined ? part.minCalc : part.min,
            max = part.maxCalc !== undefined ? part.maxCalc : part.max;

          if (part.forceWeight !== undefined) {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            (partObject3D as Mesh).morphTargetInfluences![0] = part.forceWeight;
          } else {
            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            (partObject3D as Mesh).morphTargetInfluences![0] =
              (cells - min) / (max - min);
          }
        }

        if (part.resetPos) {
          if (typeof part.resetPos === "boolean") {
            partObject3D.position.copy(new Vector3());
          } else {
            partObject3D.position.copy(part.resetPos);
          }
        }

        holder3D.add3D(part.partName || partName, partObject3D);

        if (part.globalGroup) {
          addToGroup(partObject3D.uuid, part.globalGroup);
        }

        if (overridePos) {
          partObject3D.position.copy(overridePos);
        }

        if (overrideRot) {
          partObject3D.rotation.x = overrideRot.x;
          partObject3D.rotation.y = overrideRot.y;
          partObject3D.rotation.z = overrideRot.z;
        }

        const objMeasures = computeBoundingBoxHelper(partObject3D);
        if (objMeasures) measures[partObject3D.uuid] = objMeasures;
      }
    }
  });

  return { measures, partsGroups };

  function addToGroup(id: string, groupNames: string) {
    const groups = getGroupsStrArray(groupNames);

    groups.forEach((group) => {
      if (!partsGroups[id]) partsGroups[id] = [];
      partsGroups[id].push(group);
    });
  }
}

function getGroupsStrArray(groupNames: string): string[] {
  return groupNames
    .split(",")
    .map((s) => s.trim())
    .filter((v, i, a) => Boolean(v) && a.indexOf(v) === i);
}
